import { Component } from '@theme/component';

/**
 * PDP: copia un código de cupón al portapapeles (one-click), con feedback para SR y teclado.
 *
 * @typedef {Object} PdpCouponCopyRefs
 * @property {HTMLButtonElement} copyButton
 * @property {HTMLElement} status
 */

/** @extends {Component<PdpCouponCopyRefs>} */
class PdpCouponCopy extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  connectedCallback() {
    super.connectedCallback();
    const btn = this.refs.copyButton;
    btn?.addEventListener('click', this.#onCopy);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
    this.refs.copyButton?.removeEventListener('click', this.#onCopy);
  }

  #onCopy = async () => {
    const code = (this.dataset.code ?? '').trim();
    const btn = this.refs.copyButton;
    const status = this.refs.status;
    if (!code || !btn || !status) return;

    clearTimeout(this.#resetTimer);

    try {
      await copyPlainText(code);
      const copied = this.dataset.labelCopied ?? '';
      status.textContent = copied;
      btn.setAttribute('aria-label', copied);

      this.#resetTimer = setTimeout(() => {
        status.textContent = '';
        const defaultLabel = this.dataset.labelCopy ?? '';
        btn.setAttribute('aria-label', defaultLabel);
      }, 3200);
    } catch {
      const err = this.dataset.labelError ?? '';
      status.textContent = err;
    }
  };
}

/**
 * @param {string} text
 * @returns {Promise<void>}
 */
async function copyPlainText(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();

  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) {
    throw new Error('copy failed');
  }
}

if (!customElements.get('pdp-coupon-copy')) {
  customElements.define('pdp-coupon-copy', PdpCouponCopy);
}
